import { useMemo, useState, useEffect, useCallback } from "react";
import {
  Card,
  IndexTable,
  BlockStack,
  IndexFilters,
  Page,
  IndexFiltersMode,
  Text,
  Box,
  InlineStack,
  SkeletonBodyText,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { IndexTableHeading } from "@medentic/polaris-react/build/ts/src/components/IndexTable/IndexTable";
import { NonEmptyArray } from "@medentic/polaris-react/build/ts/src/types";
import { AppPageWrapper } from "@shared/ui";
import { Currency } from "@shared/ui";
import { RenderDate } from "@shared/ui";
import { ROOTS, EVENT_ROUTES } from "@app/config/constants";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import LabelWithDownload from "@features/events/components/common/LabelWithDownload";
import { NoteIcon } from "@medentic/polaris-icons";
import { usePayouts } from "@features/events/hooks/usePayouts";
import { PayoutStatusType } from "@features/events/types";

export default function EventPayouts() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Fetch payouts data
  const { payouts, pendingTotals, isLoading } = usePayouts(hcc);

  // Tabs for payout status
  const tabs = [
    { content: t("eventPayouts.all"), index: 0, id: "all" },
    { content: t("eventPayouts.pending"), index: 1, id: "pending" },
    { content: t("eventPayouts.paid"), index: 2, id: "paid" },
  ];

  // Initialize state from URL params
  const [selectedTab, setSelectedTab] = useState(() => {
    const status = searchParams.get("status");
    if (status === "pending") return 1;
    if (status === "paid") return 2;
    return 0;
  });
  const [queryValue, setQueryValue] = useState(
    () => searchParams.get("search") || ""
  );
  const [debouncedQuery, setDebouncedQuery] = useState(
    () => searchParams.get("search") || ""
  );
  const [mode, setMode] = useState(IndexFiltersMode.Default);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [queryValue]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTab === 1) params.set("status", "pending");
    if (selectedTab === 2) params.set("status", "paid");
    if (debouncedQuery) params.set("search", debouncedQuery);
    setSearchParams(params);
  }, [selectedTab, debouncedQuery, setSearchParams]);

  // Filter payouts based on tab and search
  const filteredPayouts = useMemo(() => {
    let result = [...payouts];

    // Filter by status tab
    if (selectedTab === 1) {
      result = result.filter((p) => p.payoutStatus === PayoutStatusType.PENDING);
    } else if (selectedTab === 2) {
      result = result.filter((p) => p.payoutStatus === PayoutStatusType.PAID);
    }

    // Filter by search query (event name)
    if (debouncedQuery) {
      const query = debouncedQuery.toLowerCase();
      result = result.filter((p) =>
        p.eventTitle.toLowerCase().includes(query)
      );
    }

    return result;
  }, [payouts, selectedTab, debouncedQuery]);

  // Table headings
  const headings = [
    { title: t("eventPayouts.event") },
    { title: t("eventPayouts.status") },
    { title: t("eventPayouts.ticketCount"), alignment: "end" },
    { title: t("eventPayouts.collected"), alignment: "end" },
    { title: t("eventPayouts.paidOut"), alignment: "end" },
    { title: t("eventPayouts.fees"), alignment: "end" },
    { title: t("eventPayouts.payoutDue") },
  ];

  // Handle row click - navigate to transactions filtered by event
  const handleRowClick = useCallback(
    (eventId: string) => {
      navigate(
        `${ROOTS.EVENTS}/${hcc}/${EVENT_ROUTES.TRANSACTIONS}?eventId=${eventId}`
      );
    },
    [hcc, navigate]
  );

  // Table rows
  const rowMarkup = filteredPayouts.map((p, idx) => (
    <IndexTable.Row
      id={p.id || p.eventId}
      key={p.id || p.eventId}
      position={idx}
      onClick={() => handleRowClick(p.eventId)}
    >
      <IndexTable.Cell>
        {p.note ? (
          <LabelWithDownload
            label={p.eventTitle}
            tooltip={p.note}
            icon={NoteIcon}
            iconAtStart
            truncate
          />
        ) : (
          <Text as="span" variant="bodySm" truncate>
            {p.eventTitle}
          </Text>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <EventBadgeRenderer slug={p.payoutStatus} entity="paymentPayout" />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" alignment="end">
          {p.ticketsCount}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" alignment="end">
          <Currency value={p.collectedAmount} currency={p.currency} />
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <LabelWithDownload
          label={<Currency value={p.paidOutNetAmount} currency={p.currency} />}
          downloadLink={p.paidOutInvoiceLink}
          tooltip={t("eventPayouts.downloadPayoutInvoice", { invoiceId: p.paidOutInvoiceId })}
        />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <LabelWithDownload
          label={<Currency value={p.fees} currency={p.currency} />}
          downloadLink={p.feesInvoiceLink}
          tooltip={t("eventPayouts.downloadFeesInvoice", { invoiceId: p.feesInvoiceId })}
        />
      </IndexTable.Cell>
      <IndexTable.Cell>
        {p.payoutDue ? (
          <RenderDate date={p.payoutDue} isAgoFormat={true} reverseTooltip />
        ) : (
          "-"
        )}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t("eventPayouts.title")}
        subtitle={t("eventPayouts.subtitle")}
      >
        <BlockStack gap="400">
          {/* Pending Payout Summary Card - only show if there are pending payouts */}
          {isLoading ? (
            <Card>
              <SkeletonBodyText lines={3} />
            </Card>
          ) : pendingTotals ? (
            <Card>
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack gap="200">
                  <Text as="p" variant="headingXl" fontWeight="bold">
                    <Currency
                      value={pendingTotals.paidOutNetAmount}
                      currency={pendingTotals.currency}
                    />
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("eventPayouts.estimatedAmountIncludingTaxes")}
                  </Text>
                  <Box paddingBlockStart="200">
                    <Text as="p" variant="bodySm">
                      {t("eventPayouts.pendingTicketsCount", {
                        count: pendingTotals.ticketsCount,
                      })}
                    </Text>
                  </Box>
                </BlockStack>
              </InlineStack>
            </Card>
          ) : null}

          {/* Payouts Table */}
          <Card padding="0">
            <BlockStack gap="0">
              <IndexFilters
                tabs={tabs}
                selected={selectedTab}
                onSelect={setSelectedTab}
                queryValue={queryValue}
                queryPlaceholder={t("eventPayouts.searchPlaceholder")}
                onQueryChange={setQueryValue}
                onQueryClear={() => setQueryValue("")}
                filters={[]}
                cancelAction={{
                  onAction: () => {
                    setSelectedTab(0);
                    setQueryValue("");
                  },
                  disabled: false,
                  loading: false,
                }}
                appliedFilters={[]}
                onClearAll={() => {
                  setSelectedTab(0);
                  setQueryValue("");
                }}
                mode={mode}
                setMode={setMode}
              />
              <IndexTable
                resourceName={{
                  singular: t("eventPayouts.payout"),
                  plural: t("eventPayouts.payoutsPlural"),
                }}
                itemCount={filteredPayouts.length}
                selectable={false}
                headings={headings as NonEmptyArray<IndexTableHeading>}
              >
                {rowMarkup}
              </IndexTable>
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppPageWrapper>
  );
}

